import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { bandColour } from './resumeCheck';

/**
 * The score as an arc.
 *
 * A bar says "this much of a line". An arc says "this much out of what was possible",
 * which is the question somebody is actually asking, and it gives the number somewhere
 * to sit rather than floating above a rule. Drawn rather than pulled from a chart
 * library: it is two paths and a number, and a dependency for that would be heavier
 * than the page it sits on.
 */
/**
 * Walk a number up to its value once, on mount.
 *
 * A score is the one thing on this page somebody came for, and a figure that is
 * simply present gets skimmed. Counting it up takes the same half second the arc
 * takes to sweep and makes the two read as one event. It respects a request for less
 * motion by arriving at the answer immediately.
 */
function useCountUp(target: number, ms = 900): number {
  const [shown, setShown] = useState(0);
  const frame = useRef(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(target);
      return;
    }

    const started = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - started) / ms);
      // Ease out, so it decelerates into the number rather than stopping dead.
      setShown(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame.current = requestAnimationFrame(step);
    };

    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [target, ms]);

  return shown;
}

export function ScoreArc({
  value,
  size = 168,
  label,
  animate = false,
}: {
  value: number;
  size?: number;
  label?: string;
  /** Count up and sweep on mount. Off for the worked example in the hero. */
  animate?: boolean;
}) {
  const stroke = Math.round(size * 0.075);
  const radius = (size - stroke) / 2;
  // A semicircle: half the circumference is the full sweep.
  const sweep = Math.PI * radius;
  const target = Math.max(0, Math.min(100, value));
  const counted = useCountUp(target);
  const shown = animate ? counted : target;

  const tone =
    target >= 70 ? 'text-positive' : target >= 45 ? 'text-warning' : 'text-danger';

  return (
    <div className="flex flex-col items-center" style={{ width: size }}>
      <svg
        width={size}
        height={size / 2 + stroke}
        viewBox={`0 0 ${size} ${size / 2 + stroke}`}
        role="img"
        aria-label={`${shown} out of 100`}
      >
        <path
          d={`M ${stroke / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - stroke / 2} ${size / 2}`}
          fill="none"
          className="stroke-surface-raised"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        <path
          d={`M ${stroke / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - stroke / 2} ${size / 2}`}
          fill="none"
          className={cn('transition-[stroke-dashoffset] duration-1000 ease-out', tone)}
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={sweep}
          strokeDashoffset={sweep - (sweep * shown) / 100}
        />
      </svg>

      <div className="flex flex-col items-center" style={{ marginTop: -size * 0.165 }}>
        <p
          className="font-display font-semibold leading-none tracking-[-0.035em] tabular-nums text-ink"
          style={{ fontSize: Math.round(size * 0.23) }}
        >
          {shown}
          <span className="text-[0.38em] font-normal tracking-normal text-ink-subtle">/100</span>
        </p>
        {label && (
          <p
            className={cn('mt-1.5 font-bold uppercase tracking-[0.14em]', tone)}
            style={{ fontSize: Math.round(size * 0.072) }}
          >
            {label}
          </p>
        )}
      </div>
    </div>
  );
}

export function Meter({
  label,
  value,
  animate = false,
  before,
}: {
  label: string;
  value: number;
  animate?: boolean;
  /** The same measure before their changes, to show the move. */
  before?: number;
}) {
  const delta = before === undefined ? 0 : value - before;
  const [filled, setFilled] = useState(!animate);
  useEffect(() => {
    if (!animate) return;
    const id = requestAnimationFrame(() => setFilled(true));
    return () => cancelAnimationFrame(id);
  }, [animate]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-subtle">{label}</span>
        <span className="text-[13px] font-semibold tabular-nums text-ink">
          {before !== undefined && delta !== 0 && (
            <span className={cn('mr-1.5 text-[11.5px]', delta > 0 ? 'text-positive' : 'text-danger')}>
              {delta > 0 ? `+${delta}` : delta}
            </span>
          )}
          {value}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-raised">
        <div
          className={cn('h-full rounded-full transition-[width] duration-700 ease-out', bandColour(value))}
          style={{ width: filled ? `${Math.max(2, value)}%` : '0%' }}
        />
      </div>
    </div>
  );
}
